import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  ManageSearch as ManageSearchIcon,
  TravelExplore as TravelExploreIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { monitoringAPI } from '../services/api';

const HOMER_BASE = 'http://sipcapture.sandbox-sngl.com:9080';

function buildHomerUrl(sipCallId, startEpoch, endEpoch) {
  if (!sipCallId) return null;
  const from = ((startEpoch || endEpoch) - 300) * 1000;
  const to = ((endEpoch || startEpoch) + 300) * 1000;
  const query = JSON.stringify({
    timestamp: { from, to },
    param: {
      search: {
        '1_call': { id: '', callid: [sipCallId], uuid: [] },
      },
      location: {},
      transaction: { call: true, registration: false, rest: false },
      id: {},
      timezone: { value: -180, name: 'Local' },
    },
  });
  return `${HOMER_BASE}/search/result?${query}=`;
}

function buildSupervisorUrl(clusterName, conferenceId) {
  if (!clusterName || !conferenceId) return null;
  return `https://${clusterName}.singlecomm.com/supervisor/call.sc?id=${conferenceId}`;
}

export default function SearchPage() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    sip_call_id: '',
    conference_name: '',
    caller_number: '',
    destination_number: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const params = {};
    if (form.sip_call_id.trim()) params.sip_call_id = form.sip_call_id.trim();
    if (form.conference_name.trim()) params.conference_name = form.conference_name.trim();
    if (form.caller_number.trim()) params.caller_number = form.caller_number.trim();
    if (form.destination_number.trim()) params.destination_number = form.destination_number.trim();

    if (!Object.keys(params).length) {
      setError('Enter at least one search field.');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);
    try {
      const res = await monitoringAPI.searchCalls(params);
      setResults(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Search failed.');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (epoch) => {
    if (!epoch) return '—';
    return new Date(epoch * 1000).toLocaleString();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Call Search
      </Typography>

      {/* Search form */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          component="form"
          onSubmit={handleSearch}
          sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}
        >
          <TextField
            label="Call ID (sip_call_id)"
            name="sip_call_id"
            value={form.sip_call_id}
            onChange={handleChange}
            size="small"
            sx={{ minWidth: 280 }}
            inputProps={{ style: { fontFamily: 'monospace' } }}
          />
          <TextField
            label="Conference"
            name="conference_name"
            value={form.conference_name}
            onChange={handleChange}
            size="small"
            sx={{ minWidth: 160 }}
            inputProps={{ style: { fontFamily: 'monospace' } }}
          />
          <TextField
            label="From (optional)"
            name="caller_number"
            value={form.caller_number}
            onChange={handleChange}
            size="small"
            sx={{ minWidth: 160 }}
          />
          <TextField
            label="To (optional)"
            name="destination_number"
            value={form.destination_number}
            onChange={handleChange}
            size="small"
            sx={{ minWidth: 160 }}
          />
          <Button
            type="submit"
            variant="contained"
            startIcon={<SearchIcon />}
            disabled={loading}
          >
            Search
          </Button>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {results && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {results.total} result{results.total !== 1 ? 's' : ''} found
            {results.total > results.results.length ? ` (showing first ${results.results.length})` : ''}
          </Typography>
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Call ID</TableCell>
                  <TableCell>From</TableCell>
                  <TableCell>To</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Hangup</TableCell>
                  <TableCell>Switch</TableCell>
                  <TableCell align="center">Links</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.results.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        No calls found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {results.results.map((call, idx) => {
                  const homerUrl = buildHomerUrl(call.sip_call_id, call.start_epoch, call.end_epoch);
                  const supervisorUrl = buildSupervisorUrl(call.cluster_name, call.conference);
                  return (
                    <TableRow key={call.sip_call_id || call.call_uuid || idx} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.78rem' }}>
                        {formatTime(call.start_epoch)}
                      </TableCell>
                      <TableCell
                        sx={{ fontFamily: 'monospace', fontSize: '0.75rem', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={call.sip_call_id}
                      >
                        {call.sip_call_id || '—'}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>
                        {call.caller || '—'}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>
                        {call.destination || '—'}
                      </TableCell>
                      <TableCell>
                        {call.duration != null ? `${call.duration}s` : '—'}
                        {call.billsec != null && call.billsec !== call.duration ? ` / ${call.billsec}s` : ''}
                      </TableCell>
                      <TableCell>
                        <Chip label={call.hangup_cause || 'UNKNOWN'} size="small" />
                      </TableCell>
                      <TableCell>
                        <Chip label={call.switch || '—'} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="CDR / SIP Trace">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/cdr/${call.sip_call_id || call.call_uuid}`)}
                          >
                            <ManageSearchIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {supervisorUrl && (
                          <Tooltip title={`Open in cluster supervisor (${call.cluster_name})`}>
                            <IconButton
                              size="small"
                              component="a"
                              href={supervisorUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {homerUrl && (
                          <Tooltip title="SIP Trace in Homer">
                            <IconButton
                              size="small"
                              component="a"
                              href={homerUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <TravelExploreIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </Box>
  );
}
